/**
 * Query execution and result schemas
 * Consolidated from useCardData.ts, useMbqlQuery.ts
 */

import { z } from 'zod';
import { fieldMetadataSchema } from './database';

// ============================================================================
// Query Result Schemas
// ============================================================================

/**
 * Query result column schema
 */
export const queryResultColumnSchema = z.object({
  name: z.string(),
  display_name: z.string(),
  base_type: z.string().optional(),
  effective_type: z.string().optional(),
  semantic_type: z.string().nullable().optional(),
  field_ref: z.array(z.unknown()).optional(),
  source: z.string().optional(),
});

export type QueryResultColumn = z.infer<typeof queryResultColumnSchema>;

/**
 * Query result data schema
 */
export const queryResultDataSchema = z.object({
  rows: z.array(z.array(z.unknown())),
  cols: z.array(queryResultColumnSchema),
  native_form: z
    .object({
      query: z.string().optional(),
      params: z.array(z.unknown()).nullable().optional(),
    })
    .optional(),
  results_metadata: z
    .object({
      columns: z.array(fieldMetadataSchema).optional(),
    })
    .optional(),
  insights: z.array(z.unknown()).nullable().optional(),
  results_timezone: z.string().optional(),
});

export type QueryResultData = z.infer<typeof queryResultDataSchema>;

/**
 * Full query result schema
 */
export const queryResultSchema = z.object({
  data: queryResultDataSchema,
  database_id: z.number().optional(),
  started_at: z.string().optional(),
  json_query: z.record(z.unknown()).optional(),
  average_execution_time: z.number().nullable().optional(),
  status: z.string(),
  context: z.string().optional(),
  row_count: z.number(),
  running_time: z.number().optional(),
});

export type QueryResult = z.infer<typeof queryResultSchema>;

// ============================================================================
// Dataset Query Schemas (MBQL and Native)
// ============================================================================

/**
 * MBQL dataset_query structure
 */
export const mbqlQuerySchema = z.object({
  database: z.number(),
  type: z.literal('query'),
  query: z.object({
    'source-table': z.number().optional(),
    aggregation: z.array(z.unknown()).optional(),
    breakout: z.array(z.unknown()).optional(),
    filter: z.unknown().optional(),
    'order-by': z.array(z.unknown()).optional(),
    limit: z.number().optional(),
    joins: z.array(z.unknown()).optional(),
  }),
});

/**
 * Native (SQL) dataset_query structure
 */
export const nativeQuerySchema = z.object({
  database: z.number(),
  type: z.literal('native'),
  native: z.object({
    query: z.string(),
    'template-tags': z.record(z.unknown()).optional(),
  }),
});

/**
 * Dataset query union - either MBQL or native SQL
 */
export const datasetQuerySchema = z.union([mbqlQuerySchema, nativeQuerySchema]);

export type DatasetQuery = z.infer<typeof datasetQuerySchema>;
export type MbqlQuery = z.infer<typeof mbqlQuerySchema>;

// ============================================================================
// Query Parameters
// ============================================================================

/**
 * Parameter for query execution
 */
export type QueryParameter = {
  type: string;
  value: unknown;
  id?: string;
  target?: unknown;
};

/**
 * Dashboard parameter for querying cards via dashboard endpoint
 */
export type DashboardQueryParameter = {
  id: string;
  type: string;
  value: unknown;
  target: unknown[];
};

// ============================================================================
// Field Values
// ============================================================================

/**
 * Field values response schema
 * GET /api/field/{id}/values
 */
export const fieldValuesSchema = z.object({
  values: z.array(z.array(z.unknown())),
  field_id: z.number().optional(),
  has_more_values: z.boolean().optional(),
});

export type FieldValuesResponse = z.infer<typeof fieldValuesSchema>;

// ============================================================================
// Utilities
// ============================================================================

/**
 * Parse query result with error handling
 * Extracted from multiple hooks to avoid duplication
 */
export const parseQueryResult = (data: unknown): QueryResult => {
  const parsed = queryResultSchema.safeParse(data);
  if (!parsed.success) {
    // oxlint-disable-next-line no-console
    console.error('Failed to parse query result:', parsed.error);
    throw new Error('Failed to parse query result');
  }
  return parsed.data;
};

/**
 * Extract values from field values response
 * The API returns values as [[value1], [value2], ...] format
 */
export const extractFieldValuesFromResponse = (
  response: FieldValuesResponse | undefined
): unknown[] => {
  if (!response?.values) return [];

  return response.values
    .map(arr => arr[0])
    .filter(value => value !== null && value !== undefined);
};
